import React, { useState } from "react";

import { FormBox, FormField, FormLabel } from "@impero-com/ui";
import { TimePicker } from "antd";
import { cx } from "class-variance-authority";
import moment, { Moment } from "moment-timezone";

import { getParentElement } from "../../../../utils/dom";
import { ERRORED_CLASSNAME } from "../../../../utils/form/erroredElements";
import * as theme from "../theme.css";

import { buildTimePickerChangeDate } from "./utils";

export interface TimePickerInputBaseProps {
    nodeAfter?: React.ReactNode;
    format?: string;
    disabled?: boolean;
    styleVariant?: "normal" | "borderless";
    label?: React.ReactNode;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

interface TimePickerInputProps extends TimePickerInputBaseProps {
    value: Date | null;
    error?: string | undefined;
    onChange?: (value: Date | null) => void;
    onBlur?: () => void;
    fieldRef?: React.RefCallback<HTMLElement>;
    allowClear?: boolean;
}

/**
 * Basic form component based on ant design's TimePicker
 */
export const TimePickerInput = ({
    label,
    styleVariant,
    value,
    error,
    onChange,
    onBlur,
    fieldRef,
    nodeAfter,
    format = "HH:mm",
    disabled = false,
    allowClear = false,
    getPopupContainer = getParentElement,
}: TimePickerInputProps) => {
    const handleChange = (time: Moment | null) => {
        // time is a Moment with a new date
        if (!onChange) {
            return;
        }
        if (time === null) {
            onChange(null);
            return;
        }
        const newDate = buildTimePickerChangeDate(time, value ?? new Date());
        onChange(newDate);
    };

    const [editing, setEditing] = useState(false);

    const handleBlur = () => {
        setEditing(false);
        onBlur?.();
    };

    const bordered =
        styleVariant !== "borderless" || editing || error !== undefined;

    return (
        <FormBox>
            <FormField
                error={error}
                lite={label === undefined}
                className={cx(error && ERRORED_CLASSNAME)}
            >
                {label && <FormLabel disabled={disabled}>{label}</FormLabel>}
                <div
                    className={cx(theme.inputWithText, theme.positionRelative)}
                >
                    <TimePicker
                        format={format}
                        ref={fieldRef}
                        className={theme.timePicker}
                        popupClassName={theme.timePickerPopup}
                        disabled={disabled}
                        value={value ? moment(value) : null}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        bordered={bordered}
                        allowClear={allowClear}
                        getPopupContainer={getPopupContainer}
                    />
                    {nodeAfter}
                </div>
            </FormField>
        </FormBox>
    );
};
